import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const LAST_SEEN_KEY = "dtscotslad:last-seen";        // localStorage — persists across sessions
const DISMISSED_KEY = "dtscotslad:whats-new-dismissed"; // sessionStorage — one dismissal per tab session
const MIN_GAP_HOURS = 12; // don't nag more than twice a day

/**
 * "What's new since your last visit" strip.
 *
 * - First-ever visit: silently records `now` in localStorage, no strip shown.
 * - Return visit (>= 12h later): calls /api/whats-new?since=lastSeen; if there are
 *   updates AND the user hasn't already dismissed this session, shows the strip.
 * - Dismiss: updates lastSeen to now + sets sessionStorage flag so it stays hidden
 *   for the rest of the tab session.
 */
export default function WhatsNew() {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(true); // hidden until we've decided to show it

    useEffect(() => {
        // Guard: if this tab session already dismissed, stay hidden
        if (sessionStorage.getItem(DISMISSED_KEY) === "1") return;

        const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
        const nowIso = new Date().toISOString();

        // First-ever visit — silently seed and bail
        if (!lastSeen) {
            localStorage.setItem(LAST_SEEN_KEY, nowIso);
            return;
        }

        // Rate-limit: don't run within MIN_GAP_HOURS of the previous check
        const hoursSince = (Date.now() - new Date(lastSeen).getTime()) / 3_600_000;
        if (hoursSince < MIN_GAP_HOURS) return;

        api.get("/whats-new", { params: { since: lastSeen } })
            .then((r) => {
                if (r.data?.has_updates) {
                    setData(r.data);
                    setDismissed(false);
                } else {
                    // No updates — refresh the timestamp so we don't re-poll for MIN_GAP_HOURS
                    localStorage.setItem(LAST_SEEN_KEY, nowIso);
                }
            })
            .catch(() => { /* silent — never block the page */ });
    }, []);

    const dismiss = () => {
        localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
        sessionStorage.setItem(DISMISSED_KEY, "1");
        setDismissed(true);
    };

    return (
        <AnimatePresence>
            {!dismissed && data?.has_updates && (
                <motion.div
                    key="whats-new"
                    initial={{ y: -60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -60, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="relative z-40 border-b border-[var(--sb-brand)]/40 bg-[var(--sb-surface)]"
                    style={{ background: "linear-gradient(90deg, rgba(11,31,58,0.95) 0%, rgba(20,42,77,0.98) 50%, rgba(11,31,58,0.95) 100%)" }}
                    data-testid="whats-new-strip"
                >
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2 shrink-0 text-[var(--sb-brand)]">
                            <Sparkles size={14} />
                            <span className="text-[10px] tracking-[0.32em] uppercase">Since your last visit</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap text-sm text-[var(--sb-text-dim)]">
                            {data.items.map((item, i) => (
                                <span key={item.type} className="flex items-center gap-2">
                                    {i > 0 && <span className="opacity-40">·</span>}
                                    <Link
                                        to={item.link}
                                        className="inline-flex items-center gap-1.5 hover:text-[var(--sb-text)] transition-colors group"
                                        onClick={dismiss}
                                        data-testid={`whats-new-${item.type}`}
                                    >
                                        <span className="font-serif text-base tabular-nums text-[var(--sb-text)]">{item.count}</span>
                                        <span>{item.label}</span>
                                        <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                                    </Link>
                                </span>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={dismiss}
                            aria-label="Dismiss"
                            className="ml-auto text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] transition-colors p-1"
                            data-testid="whats-new-dismiss"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
